import { fromSearchServiceResultItemToSearchStateListItem } from 'data/model/search'
import { setRecentItem } from 'data/reducer/search'
import { deserializeE, deserializePlaceV0, deserializeS } from 'lib/place'
import { isEmptyId, isHereId, isPlaceVE, isPlaceVS } from 'lib/place/util'
import { createPlatform } from 'map/platform'
import { createLookupRequestParams, DiscoverResultItem, SearchService } from 'map/services'
import { AnyAction } from 'redux'
import { Epic, StateObservable } from 'redux-observable'
import { catchError, filter, Observable, of, switchMap, withLatestFrom } from 'rxjs'
import { CountryAndUnit } from 'utils/formatters'
import { RecentItemType } from 'utils/storage'

import { MappingType, PdcStateItem, toPdcItem } from '../model/pdc'
import { RootState } from '../reducer'
import { fetchPdcItem, setError, setPdcItem } from '../reducer/pdc'

// TODO this needs to be moved inside config apis
const searchService = createPlatform().getSearchService() as unknown as SearchService

// TODO make it more readable, break it down into easy explained functions.
export const fetchPdcItemEpic: Epic<AnyAction, AnyAction, RootState> = (
  action$: Observable<AnyAction>,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(fetchPdcItem.match),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      return new Observable<AnyAction[]>((observer) => {
        const countryCode = state.user.countryCode
        const unit = state.app.unit
        const deserializedPdc = isHereId(action.payload.id)
          ? undefined
          : getDeserializedPdc(action.payload.id, { countryCode, unit })
        const id = deserializedPdc !== undefined ? deserializedPdc.id : action.payload.id

        if (isEmptyId(id)) {
          const at =
            action.payload.at !== undefined
              ? `${action.payload.at.lat},${action.payload.at.lng}`
              : `${state.app.browserMapPosition.lat},${state.app.browserMapPosition.lng}`
          const lang = state.app.language
          searchService.reverseGeocode(
            { at, lang },
            ({ items }) => {
              const pdcItem: PdcStateItem =
                deserializedPdc !== undefined
                  ? { ...deserializedPdc, id: items[0].id }
                  : {
                      addressLabel: items[0].address.label,
                      id: items[0].id,
                      position: items[0].position,
                      title: items[0].title,
                    }
              observer.next([setPdcItem(pdcItem)])
              observer.complete()
            },
            (error) => {
              console.error('"/reverseGeocode" request error', error)
              observer.error(error)
            }
          )
        } else {
          const searchItem = state.search.searchItemList.find((item) => item.id === id)
          if (searchItem !== undefined) {
            const pdcItem = toPdcItem(MappingType.SearchStateListItem, searchItem, {
              countryCode,
              unit,
            })
            observer.next([setPdcItem(pdcItem), setRecentItem(searchItem, RecentItemType.place)])
            observer.complete()
          } else {
            const lang = state.app.language

            searchService.lookup(
              createLookupRequestParams(id, lang),
              (item: DiscoverResultItem) => {
                const pdcItem = toPdcItem(
                  MappingType.DiscoverResultItem,
                  item,
                  { countryCode, unit },
                  state.user.currentPosition.coordinates
                )
                const recentItem = fromSearchServiceResultItemToSearchStateListItem(
                  item,
                  { countryCode, unit },
                  state.user.currentPosition.coordinates
                )
                observer.next([
                  setPdcItem(pdcItem),
                  setRecentItem(recentItem, RecentItemType.place),
                ])
                observer.complete()
              },
              (error) => {
                // TODO make it more readable, break it down into easy explained functions.
                // Reverse geocodes when lookup fails, which works as a complementary source of the serialized data.
                if (deserializedPdc !== undefined && deserializedPdc.position !== undefined) {
                  const at = `${deserializedPdc.position.lat},${deserializedPdc.position.lng}`
                  const lang = state.app.language
                  searchService.reverseGeocode(
                    { at, lang },
                    ({ items }) => {
                      const id = deserializedPdc.id !== undefined ? deserializedPdc.id : items[0].id
                      const title =
                        deserializedPdc.title !== undefined || deserializedPdc.title !== ''
                          ? deserializedPdc.title
                          : items[0].title
                      const addressLabel =
                        deserializedPdc.addressLabel !== undefined ||
                        deserializedPdc.addressLabel !== ''
                          ? deserializedPdc.addressLabel
                          : items[0].address.label
                      const pdcItem: PdcStateItem = {
                        addressLabel,
                        id,
                        position: items[0].position,
                        title,
                      }
                      observer.next([setPdcItem(pdcItem)])
                      observer.complete()
                    },
                    (error) => {
                      console.error('"/reverseGeocode" request error', error)
                      observer.error(error)
                    }
                  )
                } else {
                  console.error('"/lookup" request error', error)
                  observer.error(error)
                }
              }
            )
          }
        }
      }).pipe(
        switchMap((actions: AnyAction[]) => of(...actions)),
        catchError((error: string) => of(setError(error)))
      )
    })
  )

function getDeserializedPdc(
  placesId: string | undefined,
  countryAndUnit: CountryAndUnit
): PdcStateItem | undefined {
  try {
    if (isPlaceVS(placesId)) {
      return toPdcItem(MappingType.PlaceS, deserializeS(placesId), countryAndUnit)
    }

    if (isPlaceVE(placesId)) {
      return toPdcItem(MappingType.PlaceE, deserializeE(placesId), countryAndUnit)
    }

    return !isEmptyId(placesId)
      ? toPdcItem(MappingType.PlaceV0, deserializePlaceV0(placesId), countryAndUnit)
      : undefined
  } catch (error) {
    return undefined
  }
}
